import { Grid, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { SMARTRR_CUSTOM_ATTRIBUTES_VALUE } from "@smartrr/shared/constants";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import {
  ISmartrrInfoCustomAttributes,
  isCPSLineItemApartOfBundle,
  isCPSLineItemBundleParent,
} from "@smartrr/shared/utils/bundles";
import { collapseProducts } from "@smartrr/shared/utils/collapseProducts";
import { NestedKeyOf, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { getPurchasableFromPurchasableMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import React, { useEffect, useRef, useState } from "react";

import { HiddenProductsNumber } from "@customer-app/account/views/OperationAquarius/components/HiddenProductsNumber";
import { WIDTH_SIZES } from "@customer-app/account/views/OperationAquarius/components/ProductImage";
import { useCustomerPortalVariantToPurchasableMap } from "@customer-app/state/reducers/purchasables";

import { useSmartrrTheme } from "../../../../../../../hooks/useSmartrrTheme";
import { useMobileQuery } from "../../../../../../../utils";
import { formatDeliveryPrice } from "../../../../../../../utils/Pricing/Delivery";
import { useCurrentBundle } from "../../../../../../hooks/useCurrentBundle";
import { useGetAllSellingPlanGroups } from "../../../../../../hooks/useGetAllSellingPlanGroups";
import { useUnavailableVariantsAndDeliveries } from "../../../../../../hooks/useUnavailableVariantsAndDeliveries";
import { GridWrapper, OrderItemImage } from "../../../../constants";
import { sortAndFilterLineItemsForDisplay } from "../../../../V2SubscriptionManagement/libs/utils";
import { formatProductAndVariantNameForDisplay } from "@smartrr/shared/utils/displayUtils";

interface Props {
  delivery: PurchaseStateAndDelivery;
  imageSize?: keyof typeof WIDTH_SIZES;
  hideAddons?: boolean;
}

export function FutureOrderModule({ delivery, imageSize, hideAddons }: Props) {
  const isMobile = useMobileQuery();
  const theme = useSmartrrTheme();
  const { unavailableVariantsAndDeliveries } = useUnavailableVariantsAndDeliveries();
  const [numOfHidden, setNumOfHidden] = useState<number | null>();
  const { purchaseState } = delivery;
  const variantToPurchasableMap = useCustomerPortalVariantToPurchasableMap();
  const skdIdx = delivery.indexFromScheduleStart;
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setNumOfHidden(collapseProducts(gridRef, isMobile, imageSize!));
  }, [gridRef, imageSize, isMobile, purchaseState]);

  let singleProductName = "";
  let variantQty = 0;

  const { bundle } = useCurrentBundle(delivery);

  const sortedAndFilteredLineItems = sortAndFilterLineItemsForDisplay(purchaseState, skdIdx, hideAddons);

  const showVariantName =
    theme.modernThemeSettings?.subscriptionActions?.showVariantName! &&
    theme.modernThemeSettings?.showHide.showVariantName;

  return (
    <React.Fragment>
      <GridWrapper>
        <Grid
          container
          spacing={"10px"}
          ref={gridRef}
          sx={{ alignItems: "center", position: "relative", width: "max-content" }}
        >
          {sortedAndFilteredLineItems.map(item => {
            const purchasable = getPurchasableFromPurchasableMap(variantToPurchasableMap, item.vnt);

            if (!purchasable) {
              return <React.Fragment key={item.id} />;
            }

            singleProductName = formatProductAndVariantNameForDisplay({
              purchasableName: purchasable.purchasableName,
              purchasableVariantName: item.vnt.purchasableVariantName,
              showVariant: showVariantName,
            });

            const isUnavailable =
              unavailableVariantsAndDeliveries.vnts.some(vnt => vnt.id === item.vnt.id) ||
              unavailableVariantsAndDeliveries.purchasables.some(p => p.id === purchasable.id);

            const isBundleParent = isCPSLineItemBundleParent(item);

            if (bundle && isCPSLineItemApartOfBundle(item)) {
              if (isBundleParent && item.customAttributes?.length) {
                let tempQty = 0;
                const customAttributes = item.customAttributes.find(
                  ele => ele.key === SMARTRR_CUSTOM_ATTRIBUTES_VALUE
                );

                const smartrrCustomAttributesParsed: ISmartrrInfoCustomAttributes = customAttributes?.value
                  ? JSON.parse(customAttributes.value)
                  : {};

                for (const parsedItem of smartrrCustomAttributesParsed.items) {
                  tempQty += parsedItem.quantity;
                }

                variantQty += tempQty;
                return (
                  <Grid item key={item.id} sx={{ width: "max-content" }}>
                    <OrderItemImage
                      purchasableVariant={item.vnt}
                      quantity={-1}
                      size={imageSize}
                      disabled={isUnavailable}
                    />
                  </Grid>
                );
              }
              return <React.Fragment />;
            }

            variantQty += item.quantity;

            return (
              <Grid item key={item.id} sx={{ width: "max-content" }}>
                <OrderItemImage
                  purchasableVariant={item.vnt}
                  quantity={item.quantity}
                  size={imageSize}
                  disabled={isUnavailable}
                />
              </Grid>
            );
          })}
          {numOfHidden ? (
            <HiddenProductsNumber isMobile={isMobile} imageSize="medium" numOfHidden={numOfHidden} />
          ) : null}
        </Grid>
      </GridWrapper>
      <FutureOrderModulePricing
        delivery={delivery}
        isInManageSubscriptionView={imageSize === "small"}
        productName={singleProductName}
        quantity={variantQty}
      />
    </React.Fragment>
  );
}

export const FutureOrderModulePricing = ({
  isInManageSubscriptionView,
  quantity,
  productName,
  delivery,
}: {
  isInManageSubscriptionView: boolean;
  quantity: number;
  productName: string;
  delivery: PurchaseStateAndDelivery;
}) => {
  const allGroups = useGetAllSellingPlanGroups();
  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems={"center"}
      direction="row"
      sx={{
        margin: "15px 0",
        ...(isInManageSubscriptionView && {
          margin: "7px 0px 0px 0px !important",
        }),
      }}
    >
      <Typography
        variant={isInManageSubscriptionView ? "caption1" : "body1"}
        className={prefixClassName("product-details", "product-name")}
        data-testid={prefixClassName("product-details")}
      >
        {quantity > 1 || !productName
          ? typedLocalize("general.itemCount" as NestedKeyOf<object>, {
              count: quantity,
            })
          : productName}
      </Typography>

      <Typography
        variant={isInManageSubscriptionView ? "caption2" : "body2"}
        className={prefixClassName("product-price")}
        data-testid={prefixClassName("product-price")}
      >
        {delivery.paymentMultipleDueOnDate > 0
          ? formatDeliveryPrice(
              delivery,
              allGroups,
              { includeAddons: !isInManageSubscriptionView, pricingType: "with_discounts_and_shipping" },
              {
                removeTrailingZeros: false,
              }
            )
          : typedLocalize("general.prepaid")}
      </Typography>
    </Grid>
  );
};
